import type {
  HomeCalendarApiEvent,
  HomeCalendarEventResource,
  HomeCalendarTicketPayload,
} from "shared";
import type {
  CalendarEvent,
  CalendarEventPerson,
  CalendarEventPriority,
  CalendarEventStatus,
} from "../../types";

/**
 * Sole seam between the calendar API contract and the cards. Nothing else in
 * the section imports `HomeCalendar*` — when the dispatch-portal branch merges,
 * only the import above changes.
 */

const PRIORITIES: CalendarEventPriority[] = [
  "low",
  "medium",
  "high",
  "critical",
];

/** Unknown backend values return `undefined` so no wrong-coloured flag renders. */
const normalizePriority = (
  priority?: string | null,
): CalendarEventPriority | undefined => {
  const value = priority?.trim().toLowerCase();
  return PRIORITIES.find((known) => known === value);
};

/**
 * A breached SLA outranks the workflow status — an overdue ticket reads as
 * overdue whatever column it sits in.
 *
 * `status` is matched by substring rather than an exact enum: the contract
 * documents slugs (`in_progress`), but board statuses in this app are
 * configurable, so anything unrecognised falls back to the neutral render.
 */
const deriveTicketStatus = (
  ticket: HomeCalendarTicketPayload,
): CalendarEventStatus => {
  const sla = ticket.sla_tracking;
  if (sla?.first_response?.breached || sla?.resolution?.breached) {
    return "overdue";
  }

  const status = ticket.status?.toLowerCase() ?? "";
  if (status.includes("cancel")) return "cancelled";
  if (
    status.includes("close") ||
    status.includes("resolve") ||
    status.includes("complete")
  ) {
    return "completed";
  }
  if (status.includes("progress")) return "in_progress";
  return "scheduled";
};

const toAssignees = (
  resource?: HomeCalendarEventResource,
): CalendarEventPerson[] | undefined => {
  if (!resource?.resource_mapping_id) return undefined;
  return [
    {
      id: resource.resource_mapping_id,
      name: resource.name,
      avatar: resource.profile_image,
    },
  ];
};

const toContact = (
  ticket: HomeCalendarTicketPayload,
): CalendarEventPerson | undefined => {
  const name = [ticket.customer_user_first_name, ticket.customer_user_last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  if (!name) return undefined;

  return {
    id: ticket.customer_user_id ?? name,
    name,
    isPrivileged: ticket.is_privileged_user,
  };
};

const mapEvent = (event: HomeCalendarApiEvent): CalendarEvent => {
  const base = {
    id: event.id,
    title: event.title,
    eventType: event.event_type,
    startAt: event.start,
    assignees: toAssignees(event.resource),
  };

  if (event.event_type === "ticket" && event.ticket) {
    const { ticket } = event;
    return {
      ...base,
      status: deriveTicketStatus(ticket),
      priority: normalizePriority(ticket.priority),
      ticketId: ticket.u_id,
      ticketMappingId: ticket.mapping_id,
      customerName: ticket.customer_name ?? undefined,
      contact: toContact(ticket),
    };
  }

  if (event.event_type === "recurring-ticket" && event.recurring_ticket) {
    const recurring = event.recurring_ticket;
    return {
      ...base,
      status: "scheduled",
      priority: normalizePriority(recurring.priority),
      boardName: recurring.board_name ?? undefined,
      category: "Recurring",
      recurringTicketId: recurring.recurring_ticket_id,
      description: recurring.description,
    };
  }

  return {
    ...base,
    status: "scheduled",
    category: event.activity?.activity_type,
  };
};

/** Maps the API's events to the shape the cards render. Order is preserved. */
export const mapCalendarEvents = (
  events?: HomeCalendarApiEvent[],
): CalendarEvent[] => (events ?? []).map(mapEvent);
